import React, { useEffect, useState } from 'react';
import { Platform, StyleSheet, Text, TextInput, TouchableOpacity } from 'react-native';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';

type MobileDateTimePickerType = React.ComponentType<{
  value: Date;
  mode: 'date';
  display: 'default';
  onChange: (event: any, selectedDate?: Date) => void;
}>;

interface StatsDateInputProps {
  /** ISO date string ('YYYY-MM-DD'), or '' for no date. */
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
}

/**
 * A date picker for the Stats filter bars — native `<input type="date">` on web,
 * `@react-native-community/datetimepicker` on native (lazily required, with a plain
 * YYYY-MM-DD text field as the fallback). Mirrors the pattern in ManageClientsSearchBar.tsx.
 */
export const StatsDateInput: React.FC<StatsDateInputProps> = ({ value, onChange, ariaLabel }) => {
  const isWeb = Platform.OS === 'web';
  const [showPicker, setShowPicker] = useState(false);
  const [MobilePicker, setMobilePicker] = useState<MobileDateTimePickerType | null>(null);

  useEffect(() => {
    if (isWeb) {
      return;
    }
    try {
      const mod = require('@react-native-community/datetimepicker');
      const cmp = (mod?.default ?? mod) as MobileDateTimePickerType | undefined;
      if (cmp) {
        setMobilePicker(() => cmp);
      }
    } catch (error) {
      console.warn('StatsDateInput: mobile datetime picker unavailable; using text fallback', error);
    }
  }, [isWeb]);

  if (isWeb) {
    const webStyle = {
      ...webControlStyle,
      height: 28,
      width: 150,
      colorScheme: 'light',
      color: value ? EDITOR_COLORS.textPrimary : EDITOR_COLORS.placeholder,
    } as any;
    return React.createElement('input' as any, {
      type: 'date',
      value,
      onChange: (event: any) => onChange(String(event?.target?.value ?? '')),
      style: webStyle,
      'aria-label': ariaLabel,
    });
  }

  if (MobilePicker) {
    return (
      <>
        <TouchableOpacity
          style={styles.mobileBtn}
          onPress={() => setShowPicker(true)}
          accessibilityRole="button"
          accessibilityLabel={ariaLabel}
        >
          <Text style={[styles.mobileText, !value && styles.mobilePlaceholder]}>{value || 'YYYY-MM-DD'}</Text>
        </TouchableOpacity>
        {showPicker ? (
          <MobilePicker
            value={value ? new Date(`${value}T00:00:00`) : new Date()}
            mode="date"
            display="default"
            onChange={(_event, selectedDate) => {
              setShowPicker(false);
              if (selectedDate) {
                onChange(selectedDate.toISOString().slice(0, 10));
              }
            }}
          />
        ) : null}
      </>
    );
  }

  return (
    <TextInput
      style={styles.textInput}
      value={value}
      onChangeText={onChange}
      maxLength={10}
      placeholder="YYYY-MM-DD"
      placeholderTextColor={EDITOR_COLORS.placeholder}
      accessibilityLabel={ariaLabel}
    />
  );
};

const styles = StyleSheet.create({
  mobileBtn: {
    height: 28,
    width: 150,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.background,
  },
  mobileText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  mobilePlaceholder: {
    color: EDITOR_COLORS.placeholder,
  },
  textInput: {
    ...editorText,
    height: 28,
    width: 150,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
});
